if (!customElements.get('pd-supplement-guide')) customElements.define('pd-supplement-guide', class extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.dialog = this.querySelector('dialog');
    // Shopify escapes translated strings before JSON serialization. Decode text once;
    // it is inserted only with textContent, never as HTML.
    this.config = JSON.parse(this.querySelector('[data-guide-config]').textContent, (key, value) => typeof value === 'string' ? value.replace(/&(amp|quot|apos|lt|gt|#39);/g, (entity, name) => ({ amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', '#39': "'" })[name]) : value);
    this.copy = Object.fromEntries([...this.querySelectorAll('[data-copy]')].map(element => [element.dataset.copy, element.textContent]));
    this.reset();
    const signal = this.abort.signal;
    this.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button || button.disabled) return;
      if (button.hasAttribute('data-guide-open')) this.open(button);
      if (button.hasAttribute('data-guide-close')) this.dialog.close();
      if (button.dataset.goal) {
        if (!this.config.goals.some(goal => goal.key === button.dataset.goal)) return;
        this.reset(); this.answers.goal = button.dataset.goal;
        this.renderDetails(); this.show('detail');
      }
      if (button.dataset.detail) {
        if (!this.goal()?.details.some(detail => detail.key === button.dataset.detail)) return;
        this.answers.detail = button.dataset.detail; this.answers.format = 'any';
        this.clearResult();
        if (this.formats().length > 1) { this.renderFormats(); this.show('format'); }
        else this.result();
      }
      if (button.dataset.format) {
        if (button.dataset.format !== 'any' && !this.formats().includes(button.dataset.format)) return;
        this.answers.format = button.dataset.format; this.result();
      }
      if (button.hasAttribute('data-change-format')) { this.renderFormats(); this.show('format'); }
      if (button.dataset.back === 'detail') { this.renderDetails(); this.show('detail'); }
      if (button.dataset.back === 'goals' || button.hasAttribute('data-guide-restart')) { this.reset(); this.show('goals'); }
    }, { signal });
    document.addEventListener('click', event => {
      const link = event.target.closest('a[href="#pd-supplement-guide"]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); this.open(link);
    }, { signal });
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('pd-guide-open'); this.reset();
      (this.opener?.isConnected ? this.opener : this.querySelector('[data-guide-open]'))?.focus({ preventScroll: true });
    }, { signal });
    window.addEventListener('hashchange', () => {
      if (location.hash === '#pd-supplement-guide') this.open(this.querySelector('[data-guide-open]'));
    }, { signal });
    if (location.hash === '#pd-supplement-guide') this.open(this.querySelector('[data-guide-open]'));
  }
  text(key) { return this.copy[key] || ''; }
  goal() { return this.config.goals.find(goal => goal.key === this.answers.goal); }
  detail() { return this.goal()?.details.find(detail => detail.key === this.answers.detail); }
  reset() { this.answers = { format: 'any' }; this.clearResult(); }
  clearResult() {
    this.querySelector('[data-result-products]').replaceChildren();
    this.querySelector('[data-result-more-list]').replaceChildren();
    this.querySelector('[data-result-more]').hidden = true;
    this.querySelector('[data-result-title]').textContent = '';
    this.querySelector('[data-result-summary]').textContent = '';
    this.querySelector('[data-result-empty]').hidden = true;
    this.querySelector('[data-change-format]').hidden = true;
  }
  open(opener) {
    if (this.dialog.open) return;
    this.opener = opener; this.reset(); this.dialog.showModal();
    document.documentElement.classList.add('pd-guide-open'); this.show('goals');
  }
  renderDetails() {
    const goal = this.goal(); if (!goal) return;
    this.querySelector('[data-detail-title]').textContent = goal.question;
    const options = this.querySelector('[data-detail-options]'); options.replaceChildren();
    goal.details.forEach(detail => {
      const button = document.createElement('button');
      button.type = 'button'; button.dataset.detail = detail.key; button.textContent = detail.label;
      options.append(button);
    });
  }
  productState() {
    const handles = this.detail()?.products || [];
    const rendered = handles.map(handle => this.querySelector('template[data-guide-product="' + handle + '"]')).filter(Boolean);
    const available = rendered.filter(template => template.dataset.available === 'true');
    return { handles, rendered, available };
  }
  formats() {
    return [...new Set(this.productState().available.map(template => this.config.products[template.dataset.guideProduct]?.format).filter(Boolean))];
  }
  renderFormats() {
    const options = this.querySelector('[data-format-options]'); options.replaceChildren();
    this.formats().concat('any').forEach(format => {
      const button = document.createElement('button');
      button.type = 'button'; button.dataset.format = format; button.textContent = this.text('format_' + format);
      options.append(button);
    });
  }
  show(name) {
    this.panel = name;
    this.querySelectorAll('[data-panel]').forEach(panel => { panel.hidden = panel.dataset.panel !== name; });
    this.querySelector('[data-guide-step]').textContent = this.text('step_' + name);
    const progress = this.querySelector('[data-guide-progress]');
    progress.max = 3; progress.value = name === 'goals' ? 1 : name === 'detail' ? 2 : 3;
    const selected = this.querySelector('[data-guide-selected]');
    selected.hidden = !this.goal();
    selected.textContent = [this.goal()?.label, name !== 'detail' ? this.detail()?.label : ''].filter(Boolean).join(' · ');
    const title = this.querySelector('[data-panel="' + name + '"] h2');
    title.tabIndex = -1; title.focus({ preventScroll: true }); this.dialog.scrollTop = 0;
  }
  result() {
    const detail = this.detail();
    if (!detail) { this.show('goals'); return; }
    this.clearResult();
    const state = this.productState();
    const inFormat = template => this.answers.format === 'any' || this.config.products[template.dataset.guideProduct]?.format === this.answers.format;
    const availableMatches = state.available.filter(inFormat);
    // Keep relevant formulas visible when stock changes; never imply a sold-out product is available.
    const matches = availableMatches.length ? availableMatches : state.rendered.filter(inFormat);
    const soldOut = matches.length > 0 && availableMatches.length === 0;
    this.querySelector('[data-result-title]').textContent = soldOut ? this.text('unavailable_title') : matches.length ? detail.title : this.text('empty_title');
    this.querySelector('[data-result-summary]').textContent = soldOut ? this.text('unavailable_summary') : detail.why;
    this.querySelector('[data-result-empty]').hidden = matches.length > 0;
    this.querySelector('[data-change-format]').hidden = this.formats().length <= 1;
    const holder = this.querySelector('[data-result-products]');
    matches.slice(0, 2).forEach((template, index) => {
      const fragment = template.content.cloneNode(true);
      const product = this.config.products[template.dataset.guideProduct];
      fragment.querySelector('[data-match-heading]').textContent = template.dataset.available === 'true' ? this.text(index ? 'alternative' : 'match') : this.text('sold_out');
      fragment.querySelector('[data-match-reason]').textContent = product.why;
      fragment.querySelector('[data-match-ingredients]').textContent = product.ingredients;
      fragment.querySelector('[data-match-format]').textContent = this.text('format_' + product.format);
      holder.append(fragment);
    });
    const more = this.querySelector('[data-result-more-list]');
    matches.slice(2).forEach(template => {
      const item = document.createElement('li'), link = document.createElement('a');
      link.href = template.dataset.url;
      link.textContent = this.config.products[template.dataset.guideProduct].name;
      item.append(link); more.append(item);
    });
    this.querySelector('[data-result-more]').hidden = matches.length <= 2;
    this.show('result');
  }
  disconnectedCallback() {
    if (this.dialog?.open) this.dialog.close();
    this.abort?.abort(); this.abort = null;
    document.documentElement.classList.remove('pd-guide-open');
  }
});
